import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
} from '@mui/material';
import { Delete as DeleteIcon, Person as PersonIcon, PersonAdd as PersonAddIcon, Key as KeyIcon, ContentCopy as ContentCopyIcon, CategoryOutlined as CategoryOutlinedIcon } from '@mui/icons-material';
import { event } from '../analytics';
import { 
  GET_CUSTOMER_QUERY, 
  UPDATE_CUSTOMER_MUTATION,
  GET_CURRENT_USER_WITH_ROLE,
  GET_COMPANY_USERS,
  UPDATE_USER_ROLE,
  REMOVE_USER_FROM_COMPANY,
  INVITE_USER,
  GET_PENDING_INVITATIONS,
  CANCEL_INVITATION,
  RESEND_INVITATION,
  GET_EVALUATION_API_KEYS,
  CREATE_EVALUATION_API_KEY,
  REVOKE_EVALUATION_API_KEY,
  CompanyUser,
  Invitation,
  EvaluationAPIKeyItem,
  CreateEvaluationAPIKeyResult,
} from '../graphql/settings';

interface CustomerData {
  companyName: string;
  billingEmail: string;
  plan: string;
  emailNotifications: boolean;
  webhookNotifications: boolean;
  smsNotifications: boolean;
}

const Settings: React.FC = () => {
  const { data, loading: queryLoading, error: queryError } = useQuery(GET_CUSTOMER_QUERY);
  const { data: meData, error: meError } = useQuery(GET_CURRENT_USER_WITH_ROLE);
  
  // Log any errors with the me query
  if (meError) {
    console.error('Error fetching current user:', meError);
  }
  const { data: usersData, loading: usersLoading, refetch: refetchUsers } = useQuery(GET_COMPANY_USERS);
  
  const [updateCustomer, { loading: mutationLoading }] = useMutation(UPDATE_CUSTOMER_MUTATION);
  const [updateUserRole] = useMutation(UPDATE_USER_ROLE);
  const [removeUser] = useMutation(REMOVE_USER_FROM_COMPANY);
  const [inviteUser, { loading: invitingUser }] = useMutation(INVITE_USER);
  const [cancelInvitation] = useMutation(CANCEL_INVITATION);
  const [resendInvitation] = useMutation(RESEND_INVITATION);
  
  const { data: invitationsData, refetch: refetchInvitations } = useQuery(GET_PENDING_INVITATIONS);
  const { data: evalKeysData, refetch: refetchEvalKeys } = useQuery(GET_EVALUATION_API_KEYS, { skip: !meData?.me?.isAdmin });
  const [createEvalKey, { loading: creatingEvalKey }] = useMutation(CREATE_EVALUATION_API_KEY);
  const [revokeEvalKey] = useMutation(REVOKE_EVALUATION_API_KEY);
  
  const [customer, setCustomer] = useState<CustomerData>({
    companyName: '',
    billingEmail: '',
    plan: 'Professional',
    emailNotifications: true,
    webhookNotifications: false,
    smsNotifications: false,
  });
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<CompanyUser | null>(null);
  
  // Add user form state
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');

  // Evaluation API key
  const [evalKeyName, setEvalKeyName] = useState('');
  const [evalKeySampleRate, setEvalKeySampleRate] = useState(5);
  const [createEvalKeyDialogOpen, setCreateEvalKeyDialogOpen] = useState(false);
  const [createdEvalKeyResult, setCreatedEvalKeyResult] = useState<CreateEvaluationAPIKeyResult | null>(null);
  const [revokeEvalKeyId, setRevokeEvalKeyId] = useState<string | null>(null);

  const isAdmin = meData?.me?.isAdmin || false;
  const currentUserId = meData?.me?.id;

  // Load customer data when query completes
  useEffect(() => {
    if (data?.customer) {
      setCustomer({
        companyName: data.customer.companyName,
        billingEmail: data.customer.billingEmail,
        plan: data.customer.plan,
        emailNotifications: data.customer.emailNotifications,
        webhookNotifications: data.customer.webhookNotifications,
        smsNotifications: data.customer.smsNotifications,
      });
    }
  }, [data]);

  const handleSaveProfile = async () => {
    try {
      await updateCustomer({
        variables: {
          input: {
            name: customer.companyName,
            billingEmail: customer.billingEmail,
            emailNotifications: customer.emailNotifications,
            webhookNotifications: customer.webhookNotifications,
            smsNotifications: customer.smsNotifications,
          },
        },
      });
      setSuccessMessage('Settings saved successfully!');
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage('Failed to save settings. Please try again.');
      setShowError(true);
    }
  };

  const handleNotificationChange = (type: string, value: boolean) => {
    setCustomer({
      ...customer,
      [type]: value,
    });
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      await updateUserRole({
        variables: {
          input: {
            userId,
            role: newRole,
          },
        },
      });
      setSuccessMessage('User role updated successfully!');
      setShowSuccess(true);
      refetchUsers();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      setErrorMessage(error.message || 'Failed to update user role.');
      setShowError(true);
    }
  };

  const handleDeleteClick = (user: CompanyUser) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await removeUser({
        variables: {
          userId: userToDelete.userId,
        },
      });
      setSuccessMessage(`${userToDelete.username} has been removed from the company.`);
      setShowSuccess(true);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      refetchUsers();
    } catch (error: any) {
      console.error('Error removing user:', error);
      setErrorMessage(error.message || 'Failed to remove user.');
      setShowError(true);
      setDeleteDialogOpen(false);
    }
  };

  const handleInviteUser = async () => {
    if (!newUserEmail.trim()) {
      setErrorMessage('Please enter an email address.');
      setShowError(true);
      return;
    }
    
    try {
      await inviteUser({
        variables: {
          input: {
            email: newUserEmail.trim(),
            role: newUserRole,
          },
        },
      });
      event('invite_user', { event_category: 'settings', event_action: 'invite', label: newUserEmail.trim() });
      setSuccessMessage(`Invitation sent to ${newUserEmail}`);
      setShowSuccess(true);
      setAddUserDialogOpen(false);
      setNewUserEmail('');
      setNewUserRole('viewer');
      refetchInvitations();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      setErrorMessage(error.message || 'Failed to send invitation.');
      setShowError(true);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation({
        variables: { invitationId },
      });
      setSuccessMessage('Invitation cancelled');
      setShowSuccess(true);
      refetchInvitations();
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to cancel invitation');
      setShowError(true);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation({
        variables: { invitationId },
      });
      setSuccessMessage('Invitation resent');
      setShowSuccess(true);
      refetchInvitations();
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to resend invitation');
      setShowError(true);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'editor': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const handleCreateEvalKey = async () => {
    if (!evalKeyName.trim()) {
      setErrorMessage('Please enter a name for the API key.');
      setShowError(true);
      return;
    }
    try {
      const res = await createEvalKey({
        variables: {
          input: {
            name: evalKeyName.trim(),
            sampleRatePer100: Math.max(1, Math.min(20, evalKeySampleRate)),
          },
        },
      });
      const result = res.data?.createEvaluationApiKey;
      if (result) {
        setCreatedEvalKeyResult(result);
        setCreateEvalKeyDialogOpen(false);
        setEvalKeyName('');
        setEvalKeySampleRate(5);
        refetchEvalKeys();
      }
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to create evaluation API key.');
      setShowError(true);
    }
  };

  const handleRevokeEvalKey = async (id: string) => {
    try {
      await revokeEvalKey({ variables: { id } });
      setSuccessMessage('Evaluation API key revoked.');
      setShowSuccess(true);
      setRevokeEvalKeyId(null);
      refetchEvalKeys();
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to revoke key.');
      setShowError(true);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage('Copied to clipboard');
    setShowSuccess(true);
  };
  
  if (queryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (queryError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading settings: {queryError.message}</Alert>
      </Box>
    );
  }

  const companyUsers: CompanyUser[] = usersData?.companyUsers || [];
  const pendingInvitations: Invitation[] = invitationsData?.pendingInvitations || [];

  // Debug logging
  console.log('meData:', meData, 'isAdmin:', isAdmin);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Settings
        </Typography>
        {meData?.me?.role && (
          <Chip 
            label={`Your role: ${meData.me.role}`} 
            color={getRoleColor(meData.me.role) as any}
            size="small"
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Company Profile */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Company Profile
                </Typography>
                {!isAdmin && (
                  <Chip label="View Only" size="small" variant="outlined" />
                )}
              </Box>
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={customer.companyName}
                  onChange={(e) => setCustomer({ ...customer, companyName: e.target.value })}
                  sx={{ mb: 2 }}
                  disabled={mutationLoading || !isAdmin}
                  InputProps={{ readOnly: !isAdmin }}
                />
                <TextField
                  fullWidth
                  label="Billing Email"
                  type="email"
                  value={customer.billingEmail}
                  onChange={(e) => setCustomer({ ...customer, billingEmail: e.target.value })}
                  sx={{ mb: 2 }}
                  disabled={mutationLoading || !isAdmin}
                  InputProps={{ readOnly: !isAdmin }}
                />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1">Current Plan:</Typography>
                  <Chip label={customer.plan} color="primary" />
                </Box>
                {isAdmin && (
                  <Button 
                    data-ga-label="Save profile"
                    variant="contained" 
                    onClick={handleSaveProfile}
                    disabled={mutationLoading}
                    sx={{ 
                      borderRadius: 2,
                      bgcolor: '#1e3a5f',
                      '&:hover': {
                        bgcolor: '#152a4a'
                      }
                    }}
                  >
                    {mutationLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Notification Settings
                </Typography>
                {!isAdmin && (
                  <Chip label="View Only" size="small" variant="outlined" />
                )}
              </Box>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive bias alerts via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={customer.emailNotifications}
                      onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
                      disabled={mutationLoading || !isAdmin}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1e3a5f',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1e3a5f',
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Webhook Notifications"
                    secondary="Send alerts to your webhook endpoint"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={customer.webhookNotifications}
                      onChange={(e) => handleNotificationChange('webhookNotifications', e.target.checked)}
                      disabled={mutationLoading || !isAdmin}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1e3a5f',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1e3a5f',
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="SMS Notifications"
                    secondary="Critical alerts via SMS"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={customer.smsNotifications}
                      onChange={(e) => handleNotificationChange('smsNotifications', e.target.checked)}
                      disabled={mutationLoading || !isAdmin}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1e3a5f',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#1e3a5f',
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* User Management - Admin Only */}
        {isAdmin && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: '#1e3a5f' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      User Management
                    </Typography>
                    <Chip label="Admin Only" size="small" color="error" sx={{ ml: 1 }} />
                  </Box>
                  <Button
                    data-ga-label="Invite User"
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setAddUserDialogOpen(true)}
                    sx={{ 
                      borderRadius: 2,
                      bgcolor: '#1e3a5f',
                      '&:hover': { bgcolor: '#152a4a' }
                    }}
                  >
                    Invite User
                  </Button>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage users in your company. Assign roles to control access levels.
                </Typography>

                {usersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Joined</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {companyUsers.map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {user.username}
                                {user.isPrimary && (
                                  <Chip label="Primary" size="small" color="success" variant="outlined" />
                                )}
                                {user.userId === currentUserId && (
                                  <Chip label="You" size="small" color="info" variant="outlined" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              {user.userId === currentUserId ? (
                                <Chip 
                                  label={user.role.charAt(0).toUpperCase() + user.role.slice(1)} 
                                  size="small" 
                                  color={getRoleColor(user.role) as any}
                                />
                              ) : (
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={user.role}
                                    onChange={(e) => handleRoleChange(user.userId, e.target.value)}
                                    sx={{ 
                                      '& .MuiSelect-select': { py: 0.5 },
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    <MenuItem value="viewer">Viewer</MenuItem>
                                    <MenuItem value="editor">Editor</MenuItem>
                                    <MenuItem value="admin">Admin</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            </TableCell>
                            <TableCell>
                              {new Date(user.joinedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              {user.userId !== currentUserId && (
                                <IconButton 
                                  data-ga-label="Remove user"
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteClick(user)}
                                  title="Remove user from company"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {companyUsers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography color="text.secondary">
                                No users found in this company.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Pending Invitations */}
                {pendingInvitations.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Pending Invitations
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fff3e0' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sent</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Expires</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingInvitations.map((invitation) => (
                            <TableRow key={invitation.id} hover>
                              <TableCell>{invitation.email}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)} 
                                  size="small" 
                                  color={getRoleColor(invitation.role) as any}
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(invitation.createdAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                {new Date(invitation.expiresAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell align="right">
                                <Button 
                                  data-ga-label="Resend invitation"
                                  size="small" 
                                  onClick={() => handleResendInvitation(invitation.id)}
                                  sx={{ mr: 1 }}
                                >
                                  Resend
                                </Button>
                                <IconButton 
                                  data-ga-label="Cancel invitation"
                                  size="small" 
                                  color="error"
                                  onClick={() => handleCancelInvitation(invitation.id)}
                                  title="Cancel invitation"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f3ff', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Role Descriptions:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Viewer:</strong> Read-only access to all company data.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Editor:</strong> Can create, edit, and execute sequences.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Admin:</strong> Full access including user management and settings.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Evaluation API (Admin only) */}
        {isAdmin && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <KeyIcon /> Evaluation API
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      component={RouterLink}
                      to="/evaluation-types"
                      variant="outlined"
                      startIcon={<CategoryOutlinedIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Evaluation types
                    </Button>
                    <Button
                      data-ga-label="Create API Key"
                      variant="contained"
                      startIcon={<KeyIcon />}
                      onClick={() => setCreateEvalKeyDialogOpen(true)}
                      sx={{ borderRadius: 2, bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}
                    >
                      Create API Key
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create an API key and secret to POST request/response payloads for evaluation. Requests must go through the frontend URL (below); the frontend proxies to the backend. Use headers <code>X-API-Key</code> and <code>X-API-Secret</code>. Sample rate (1–20 per 100 requests) controls how many submissions are analyzed.
                  Optional <code>custom_tests</code> in the JSON body overrides defaults for that request. If omitted, defaults come from the matching{' '}
                  <strong>evaluation type</strong> (evaluation API key + <code>evaluation_key</code>) on the Evaluation types page.
                </Typography>
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Evaluation API endpoint (use this URL from your app):</Typography>
                  <Typography component="code" variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {typeof window !== 'undefined' ? `${window.location.origin}/api/v1/customers/evaluate/` : '/api/v1/customers/evaluate/'}
                  </Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sample rate</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(evalKeysData?.evaluationApiKeys || []).map((k: EvaluationAPIKeyItem) => (
                        <TableRow key={k.id} hover>
                          <TableCell>{k.name}</TableCell>
                          <TableCell><Typography variant="body2" fontFamily="monospace">{k.keyMasked}</Typography></TableCell>
                          <TableCell>{k.sampleRatePer100} per 100</TableCell>
                          <TableCell>{new Date(k.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip label={k.isActive ? 'Active' : 'Revoked'} size="small" color={k.isActive ? 'success' : 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            {k.isActive && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                                <Button data-ga-label="Revoke API key" size="small" color="error" onClick={() => setRevokeEvalKeyId(k.id)}>
                                  Revoke
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!evalKeysData?.evaluationApiKeys || evalKeysData.evaluationApiKeys.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">No evaluation API keys. Create one to submit request/response payloads for analysis.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>

      {/* Invite User Dialog */}
      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User to Company</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address of the person you want to invite. They will receive an email with a link to create their account and join your company.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Email Address"
            type="email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ mb: 2 }}
            disabled={invitingUser}
          />
          <FormControl fullWidth>
            <Typography variant="body2" sx={{ mb: 1 }}>Role</Typography>
            <Select
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value)}
              disabled={invitingUser}
            >
              <MenuItem value="viewer">Viewer - Read-only access</MenuItem>
              <MenuItem value="editor">Editor - Can create and edit sequences</MenuItem>
              <MenuItem value="admin">Admin - Full access including user management</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserDialogOpen(false)} disabled={invitingUser}>
            Cancel
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained"
            disabled={invitingUser}
            sx={{ 
              bgcolor: '#1e3a5f',
              '&:hover': { bgcolor: '#152a4a' }
            }}
          >
            {invitingUser ? <CircularProgress size={24} color="inherit" /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{userToDelete?.username}</strong> from the company? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Remove User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Evaluation API Key Dialog */}
      <Dialog open={createEvalKeyDialogOpen} onClose={() => setCreateEvalKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Evaluation API Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a key and secret to authenticate POST requests to the evaluation API. The secret is shown only once.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={evalKeyName}
            onChange={(e) => setEvalKeyName(e.target.value)}
            placeholder="e.g. Production"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Sample rate (per 100 requests)"
            value={evalKeySampleRate}
            onChange={(e) => setEvalKeySampleRate(parseInt(e.target.value, 10) || 5)}
            inputProps={{ min: 1, max: 20 }}
            helperText="How many of every 100 requests to analyze (1–20). Unsampled requests get 200 OK and are not stored."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEvalKeyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateEvalKey} variant="contained" disabled={creatingEvalKey} sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}>
            {creatingEvalKey ? <CircularProgress size={24} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show key/secret once after create */}
      <Dialog open={!!createdEvalKeyResult} onClose={() => setCreatedEvalKeyResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key Created — Copy Now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>The secret will not be shown again. Copy both key and secret now.</Alert>
          {createdEvalKeyResult && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>API Key</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TextField fullWidth size="small" value={createdEvalKeyResult.key} InputProps={{ readOnly: true }} />
                <IconButton onClick={() => copyToClipboard(createdEvalKeyResult.key)} title="Copy"><ContentCopyIcon /></IconButton>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>API Secret</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField fullWidth size="small" value={createdEvalKeyResult.secret} type="password" InputProps={{ readOnly: true }} />
                <IconButton onClick={() => copyToClipboard(createdEvalKeyResult.secret)} title="Copy"><ContentCopyIcon /></IconButton>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedEvalKeyResult(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Evaluation API Key confirmation */}
      <Dialog open={!!revokeEvalKeyId} onClose={() => setRevokeEvalKeyId(null)}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>Revoking this key will prevent new evaluation requests from being accepted. Existing results are kept.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeEvalKeyId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => revokeEvalKeyId && handleRevokeEvalKey(revokeEvalKeyId)}>Revoke</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
